let socket = null;
let messageHandlers = [];
let portfolioHandlers = [];
let matchedTradesHandlers = []; // Add handlers for matched trades
let hasAttemptedConnection = false;
let reconnectTimeout = null;
let portfolioUpdateInterval = null;
let tradeMatchHandler = null;
let messageHandler = null;
let portfolioHandler = null;
let settlementHandler = null;
let counterHandlers = [];
let errorHandler = null;
let drawUpdateHandler = null;
let roleSignalHandler = null;
let caseLoadedHandler = null;
let orderFillHandler = null;
let adminDataHandler = null;
let currentUserId = null;
let currentUserName = null;
let currentTeamId = null;
let currentTeamName = null;

// Function to properly close the WebSocket connection
const closeWebSocketConnection = () => {
  if (socket) {
    console.log("Closing WebSocket connection...");
    clearTimeout(reconnectTimeout);
    clearInterval(portfolioUpdateInterval);

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close(1000, "Normal closure");
    }
    socket = null;
    hasAttemptedConnection = false;
  }
};

// Get WebSocket URL from environment variable or use default
const getWebSocketUrl = () => {
  return process.env.REACT_APP_WEBSOCKET_URL || "ws://localhost:8000/ws";
};

export const connectWebSocket = (
  connectionId,
  url = getWebSocketUrl()
) => {
  // If we already have an open connection with the SAME connection ID, return it
  if (socket && socket.readyState === WebSocket.OPEN && socket.url.includes(connectionId)) {
    console.log("Reusing existing WebSocket connection for", connectionId);
    return socket;
  }

  // If we're in the process of connecting, don't create a new connection
  if (socket && socket.readyState === WebSocket.CONNECTING) {
    console.log("WebSocket connection already in progress");
    return socket;
  }

  // Clear any existing connection first (important for user switching)
  closeWebSocketConnection();

  console.log("Creating new WebSocket connection for", connectionId);
  const connectionUrl = `${url}?connection_id=${connectionId}`;

  socket = new WebSocket(connectionUrl);
  hasAttemptedConnection = true;

  socket.onopen = () => {
    window.addEventListener("beforeunload", closeWebSocketConnection);
    // Portfolio polling moved to App.js to avoid duplication
  };

  socket.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data);
      handleWebSocketMessage(message);
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  };

  socket.onerror = (error) => {
    console.error("WebSocket error:", error);
  };

  socket.onclose = (event) => {
    window.removeEventListener("beforeunload", closeWebSocketConnection);
    clearInterval(portfolioUpdateInterval);

    // Invalid API key — notify error handler and don't reconnect
    if (event.code === 4001) {
      if (errorHandler) {
        errorHandler({ error: "Invalid API key" });
      }
      return;
    }

    // If this wasn't a normal closure, try to reconnect once after a delay
    if (event.code !== 1000) {
      reconnectTimeout = setTimeout(() => {
        hasAttemptedConnection = false;
        connectWebSocket(connectionId, url);
      }, 1000);
    }
  };

  return socket;
};

// Add a reset function that properly cleans up before trying to connect again
export const resetWebSocketConnection = () => {
  closeWebSocketConnection();
  hasAttemptedConnection = false;
};

// Also add a manual disconnect function that can be called from App.js
export const disconnectWebSocket = () => {
  window.removeEventListener("beforeunload", closeWebSocketConnection);
  closeWebSocketConnection();
};

const handleWebSocketMessage = (message) => {
  switch (message.command) {
    case "connected":
      currentUserId = message.user_id;
      currentTeamId = message.team_id || null;
      currentTeamName = message.team_name || null;
      break;

    case "market_data":
      messageHandlers.forEach((handler) => {
        try {
          handler(message.data, message.status, message.vwap || null);
        } catch (error) {
          console.error("Error in market data handler:", error);
        }
      });
      break;

    case "portfolio":
      portfolioHandlers.forEach((handler) => {
        try {
          handler(message.response);
        } catch (error) {
          console.error("Error in portfolio handler:", error);
        }
      });
      break;

    case "order_update":
      // Trade matched — request fresh portfolio so positions update immediately
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ command: "current_portfolio" }));
        socket.send(JSON.stringify({ command: "matched_trades" }));
      }
      // Notify the current user about their fills (including teammate fills)
      if (orderFillHandler && message.matches && message.matches.length > 0) {
        for (const match of message.matches) {
          const isMyTrade = currentUserId && (match.maker_id === currentUserId || match.taker_id === currentUserId);
          const isTeamTrade = !isMyTrade && currentTeamId &&
            (match.maker_account_id === currentTeamId || match.taker_account_id === currentTeamId);
          console.log("[order_update] match:", match.symbol, match.quantity, "@", match.price,
            "maker:", match.maker_id, "taker:", match.taker_id,
            "me:", currentUserId, "team:", currentTeamId,
            "isMyTrade:", isMyTrade, "isTeamTrade:", isTeamTrade);
          if (isMyTrade || isTeamTrade) {
            const isBuyer =
              (match.taker_id === currentUserId && match.side === "buy") ||
              (match.maker_id === currentUserId && match.side === "sell") ||
              (isTeamTrade && match.taker_account_id === currentTeamId && match.side === "buy") ||
              (isTeamTrade && match.maker_account_id === currentTeamId && match.side === "sell");
            orderFillHandler({
              symbol: match.symbol,
              price: match.price,
              quantity: match.quantity,
              side: isBuyer ? "BUY" : "SELL",
            });
          }
        }
      }
      break;

    case "order_cancelled":
      // Order cancelled — request fresh portfolio to sync state
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ command: "current_portfolio" }));
      }
      break;

    case "matched_trades":
      matchedTradesHandlers.forEach((handler) => {
        try {
          handler(message.response.trades);
        } catch (error) {
          console.error("Error in matched trades handler:", error);
        }
      });
      break;

    case "trade_match":
      if (tradeMatchHandler) {
        try {
          tradeMatchHandler(message.trade);
        } catch (error) {
          console.error("Error in trade match handler:", error);
        }
      }
      break;

    case "timer_update":
      // Timer updates from the backend
      counterHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("Error in timer update handler:", error);
        }
      });
      break;

    case "trading_ended":
      // Trading ended, awaiting settlement
      if (settlementHandler) {
        settlementHandler({ ...message, is_settled: false });
      }
      counterHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("Error in trading ended handler:", error);
        }
      });
      break;

    case "trading_started":
      // Trading started
      counterHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("Error in trading status handler:", error);
        }
      });
      break;

    case "symbols_updated":
      // Symbols changed - update status
      counterHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("Error in symbols updated handler:", error);
        }
      });
      break;

    case "session_reset":
      // Session reset - notify handlers and trigger portfolio refresh
      counterHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("Error in session reset handler:", error);
        }
      });
      // Trigger portfolio refresh for all users
      // The app will request current_portfolio automatically
      break;

    case "settlement_status":
      if (settlementHandler) {
        settlementHandler(message);
      }
      break;

    case "draw_update":
      if (drawUpdateHandler) {
        try {
          drawUpdateHandler(message.state);
        } catch (error) {
          console.error("Error in draw update handler:", error);
        }
      }
      break;

    case "role_signal":
      if (roleSignalHandler) {
        try {
          roleSignalHandler(message.signal);
        } catch (error) {
          console.error("Error in role signal handler:", error);
        }
      }
      break;

    case "case_loaded":
      if (caseLoadedHandler) {
        try {
          caseLoadedHandler(message);
        } catch (error) {
          console.error("Error in case loaded handler:", error);
        }
      }
      // Also update symbols via counter handlers
      counterHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("Error in case loaded counter handler:", error);
        }
      });
      break;

    case "mini_case_loaded":
    case "draws_started":
    case "draws_stopped":
    case "roles_assigned":
      // Route through counter handlers for status updates
      counterHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error("Error in draw control handler:", error);
        }
      });
      break;

    case "leaderboard_data":
    case "admin_portfolio":
    case "bot_status":
    case "quarter_saved":
    case "combined_leaderboard":
    case "teams_data":
      if (adminDataHandler) {
        try {
          adminDataHandler(message);
        } catch (error) {
          console.error("Error in admin data handler:", error);
        }
      }
      break;

    case "error":
      if (errorHandler) {
        try {
          errorHandler(message.response);
        } catch (error) {
          console.error("Error in error handler:", error);
        }
      }
      break;

    default:
      // Silently ignore unknown commands for better performance
      break;
  }
};

export const registerMessageHandler = (handler) => {
  messageHandlers.push(handler);
  return () => {
    messageHandlers = messageHandlers.filter((h) => h !== handler);
  };
};

export const registerPortfolioHandler = (handler) => {
  portfolioHandlers.push(handler);
  return () => {
    portfolioHandlers = portfolioHandlers.filter((h) => h !== handler);
  };
};

export const registerMatchedTradesHandler = (handler) => {
  matchedTradesHandlers.push(handler);
  return () => {
    matchedTradesHandlers = matchedTradesHandlers.filter((h) => h !== handler);
  };
};

// Register handler for trade matches
export const registerTradeMatchHandler = (handler) => {
  tradeMatchHandler = handler;
  return () => {
    tradeMatchHandler = null;
  };
};

// Register handler for counter updates
export const registerCounterHandler = (handler) => {
  counterHandlers.push(handler);
  return () => {
    counterHandlers = counterHandlers.filter((h) => h !== handler);
  };
};

// Register handler for settlement status
export const registerSettlementHandler = (handler) => {
  settlementHandler = handler;
  return () => {
    settlementHandler = null;
  };
};

// Register handler for errors
export const registerErrorHandler = (handler) => {
  errorHandler = handler;
  return () => {
    errorHandler = null;
  };
};

// Register handler for draw updates
export const registerDrawUpdateHandler = (handler) => {
  drawUpdateHandler = handler;
  return () => {
    drawUpdateHandler = null;
  };
};

// Register handler for role signals
export const registerRoleSignalHandler = (handler) => {
  roleSignalHandler = handler;
  return () => {
    roleSignalHandler = null;
  };
};

// Register handler for order fill notifications
export const registerOrderFillHandler = (handler) => {
  orderFillHandler = handler;
  return () => {
    orderFillHandler = null;
  };
};

// Register handler for case loaded
export const registerCaseLoadedHandler = (handler) => {
  caseLoadedHandler = handler;
  return () => {
    caseLoadedHandler = null;
  };
};

export const sendMessage = (message) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
    return true;
  }
  return false;
};

// Add this utility function to check connection status
export const getConnectionStatus = () => {
  if (!socket) return "DISCONNECTED";

  switch (socket.readyState) {
    case WebSocket.CONNECTING:
      return "CONNECTING";
    case WebSocket.OPEN:
      return "CONNECTED"; // Changed from "OPEN" to match App.js expectations
    case WebSocket.CLOSING:
      return "CLOSING";
    case WebSocket.CLOSED:
      return "CLOSED";
    default:
      return "DISCONNECTED";
  }
};

// Register handler for admin data responses (leaderboard, portfolio)
export const registerAdminDataHandler = (handler) => {
  adminDataHandler = handler;
  return () => {
    adminDataHandler = null;
  };
};

export const getCurrentUserId = () => currentUserId;
export const getCurrentTeamId = () => currentTeamId;
export const getCurrentTeamName = () => currentTeamName;

// connectWebSocket("b", "ws://localhost:8000/ws");
